import { ArrayType } from 'common/TypeUtilities';
import { map } from 'lodash';
import { ModalReviewFragment } from 'src/gqlReactTypings.generated.d';
import { ScrollSnapCarousel } from 'src/pages/components/ScrollSnapCarousel';
import { parseDate } from 'src/shared/utils/DateTimeUtilities';
import styled from 'styled-components';
import { Colors, FontSize, FontWeight, LineHeight, Spacing } from 'web-common/src/shared/styles';
import { CarouselLineItem } from './CarouselLineItem';

const StyledModalContentReview = styled.div`
  padding: ${Spacing.SINGLE_HALF} ${Spacing.SINGLE};
`;

export const ModalContentReviews = styled.div`
  overflow-y: auto;

  ${StyledModalContentReview} {
    border-bottom: 1px solid ${Colors.GRAY_LITE_20};
  }

  ${StyledModalContentReview}:last-child {
    border-bottom: none;
  }
`;

const Name = styled.p`
  font-size: ${FontSize.M};
  font-weight: ${FontWeight.SEMI_BOLD};
  line-height: ${LineHeight.XS};
  margin: 0;
`;

const Date = styled.p`
  color: ${Colors.GRAY_DARK_10};
  font-size: ${FontSize.S};
  font-weight: ${FontWeight.MEDIUM};
  line-height: ${LineHeight.XXS};
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing.HALF};
`;

const Comment = styled.p`
  font-size: ${FontSize.S};
  line-height: ${LineHeight.S};
  margin: 0;
`;

const DishesContainer = styled.div`
  margin-top: ${Spacing.SINGLE};
`;

const LineItemsCarousel = styled(ScrollSnapCarousel)`
  justify-content: flex-start;
`;

interface ModalContentReviewProps {
  availableDishes: string[];
  review: ModalReviewFragment;
  shefId: string;
  timeZone: string;
}

export const ModalContentReview = ({
  availableDishes,
  review,
  shefId,
  timeZone,
}: ModalContentReviewProps): JSX.Element => {
  const { comment, deliveryDate, user } = review;
  const { publicName } = user;
  const dateText = parseDate(deliveryDate, timeZone).toFormat('MMM dd');

  return (
    <StyledModalContentReview>
      <TitleContainer>
        <Name>{publicName}</Name>
        <Date>{dateText}</Date>
      </TitleContainer>
      {comment ? <Comment>{comment}</Comment> : null}
      <DishesContainer>
        <LineItemsCarousel outerButtonPadding={Spacing.SINGLE}>
          {map<ArrayType<ModalReviewFragment['dishes']>>(
            review.dishes,
            (lineItem: ArrayType<ModalReviewFragment['dishes']>) => (
              <CarouselLineItem
                available={availableDishes.includes(lineItem.slug)}
                key={lineItem.id}
                lineItem={lineItem}
                shefId={shefId}
              />
            )
          )}
        </LineItemsCarousel>
      </DishesContainer>
    </StyledModalContentReview>
  );
};
